import React from "react";
import {
  Accordion,
  Box,
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  Hidden,
  Inline,
  List,
  ListItem,
  Spinner,
  Stack,
  Status,
  Text,
  Tooltip,
} from "@origin-digital/ods-core";
import { IconWatchLater } from "@origin-digital/ods-icons";
// import { ProofPoints } from "../OfferCard/ProofPoints/ProofPoints";
import { ILPGOffer } from "../../model/ILPGOffer";
import { OfferType } from "../Offers/offerTab";
import { formatWholeOrDecimalCurrency } from "../../utils/formatter";
import { OfferButtonSection } from "../OfferCard/OfferButtonSection/OfferButtonSection";
import { useOffercode } from "../../hooks/useOffercode";
import content from "./content.json";
import { InternetOfferAccordion } from "../InternetOfferAccordion/InternetOfferAccordion";

const UNIT_OF_MEASURE: Record<OfferType, string> = {
  [OfferType.EXCHANGE]: "bottle",
  [OfferType.REFILL]: "litre",
};

const PROOF_POINT_ICONS: string[] = content.proofPointIcons;
const getProofPointIcon = (index: number): string =>
  PROOF_POINT_ICONS[index] ?? PROOF_POINT_ICONS[3];

interface OfferDetailCardProps {
  offer: ILPGOffer;
  loading: boolean;
  setAddressSelectorError: (error?: string) => void;
}

export const OfferDetailCard = ({
  offer,
  loading,
  setAddressSelectorError,
}: OfferDetailCardProps) => {
  const {
    salesforceId,
    offerName,
    offerDescription,
    priceAdditionalInformation,
    priceDescription,
    bottlePricing,
    equipmentFee,
    isRefill,
    bonusBox,
    offerAdditionalInformation,
    rewardOffer,
  } = offer;

  const offercode = useOffercode()?.toLowerCase();
  const legalLines = offer.rewardOffer
    ? content.legalLinesEDR
    : content.legalLines;
  const price = bottlePricing?.[1]?.price;
  const listPrice = bottlePricing?.[1]?.listPrice;
  const discount =
    Number(formatWholeOrDecimalCurrency(listPrice)) -
    Number(formatWholeOrDecimalCurrency(price));
  const hasSavings = Number(discount) > 0;
  const unitOfMeasure = isRefill
    ? UNIT_OF_MEASURE[OfferType.REFILL]
    : UNIT_OF_MEASURE[OfferType.EXCHANGE];

  return (
    <Card padding="medium" data-id="offer-detail-card">
      <Columns space="large" direction={{ xs: "column-reverse", lg: "row" }}>
        {/* Left column: Offer information */}
        <Column width="flex" data-id="offer-detail-info">
          <Stack space="xlarge">
            <Stack space="large">
              <Stack space="large">
                <Text variant="subtitle" data-id="offer-detail-name">
                  {offerName}
                </Text>
                <Stack space="xsmall">
                  <Heading variant="h2" data-id="offer-detail-description">
                    {offerDescription}
                  </Heading>
                  {bonusBox && (
                    <Status
                      icon={
                        <IconWatchLater
                          color="white"
                          variant="line"
                          size="xxxsmall"
                        />
                      }
                      tone="promote"
                      backgroundWeight="strong"
                      data-id="offer-detail-bonus-box"
                    >
                      {bonusBox}
                    </Status>
                  )}
                </Stack>
              </Stack>
            </Stack>
            {rewardOffer && (
              <Accordion
                id="reward-points-accordion"
                variant="outlined"
                image={content.rewardPointsAccordion.image}
                summary={
                  <Text weight="medium">
                    {content.rewardPointsAccordion.summary}
                  </Text>
                }
              >
                <Text>{content.rewardPointsAccordion.bodyLineOne}</Text>
              </Accordion>
            )}
            <InternetOfferAccordion key={salesforceId} />
            {/* <ProofPoints /> */}
            {(() => {
              const displayPoints = rewardOffer
                ? offerAdditionalInformation.slice(1)
                : offerAdditionalInformation;
              const startIndex = rewardOffer ? 1 : 0;
              return displayPoints.length > 0 ? (
                <List data-id="offer-proof-points">
                  {displayPoints.map((point, index) =>
                    point ? (
                      <ListItem
                        key={index}
                        icon={
                          <img
                            src={getProofPointIcon(startIndex + index)}
                            data-id={`proof-point-icon-${startIndex + index}`}
                            width="24"
                            alt=""
                          />
                        }
                      >
                        <Box marginLeft="xxsmall">{point}</Box>
                      </ListItem>
                    ) : null
                  )}
                </List>
              ) : null;
            })()}
            {!(offercode === "lpgmoveoffer" || offercode === "movers50") && (
              <Stack space="xxsmall" data-id="offer-detail-legal-text">
                <Text tone="neutral">{legalLines}</Text>
              </Stack>
            )}
            {priceAdditionalInformation && (
              <Stack space="xxsmall">
                <Text
                  variant="body-small"
                  tone="neutral"
                  data-id="offer-detail-additional-info"
                >
                  {priceAdditionalInformation}
                </Text>
              </Stack>
            )}
            {/* Buttons — mobile/tablet only */}
            <Hidden above="lg">
              <OfferButtonSection
                id={salesforceId}
                setAddressSelectorError={setAddressSelectorError}
              />
            </Hidden>
          </Stack>
        </Column>

        {/* Right column: Pricing + CTAs */}
        <Column width="5/12" data-id="offer-detail-pricing-section">
          <Stack space="xlarge">
            {/* Price box */}
            <Card
              paddingYSpecific="large"
              paddingXSpecific="large"
              backgroundColor="cardBackgroundSecondary"
              data-id="offer-detail-pricing-card"
            >
              {loading ? (
                <Stack space="small" alignX="center">
                  <Spinner
                    aria-label="loading price information"
                    size="medium"
                  />
                </Stack>
              ) : price ? (
                <Columns
                  space={{ xs: "large", lg: "none" }}
                  direction={{ xs: "row", lg: "column" }}
                >
                  {/* Cost */}
                  <Column>
                    <Stack space="small" alignX={{ xs: "left", lg: "center" }}>
                      <Text
                        variant="subtitle-small"
                        tone="neutralDark"
                        data-id="offer-detail-price-label"
                      >
                        {priceDescription || `Per ${unitOfMeasure}`}
                      </Text>
                      <Stack
                        space="xsmall"
                        alignX={{ xs: "left", lg: "center" }}
                      >
                        <Inline space="xsmall" alignY="baseline">
                          <Hidden above="lg">
                            <Heading
                              component="p"
                              weight="medium"
                              variant="h1"
                              tone={hasSavings ? "positive" : undefined}
                              data-id="offer-detail-price"
                            >
                              ${formatWholeOrDecimalCurrency(price)}
                            </Heading>
                          </Hidden>
                          <Hidden below="lg">
                            <Heading
                              component="p"
                              weight="medium"
                              variant="h2"
                              tone={hasSavings ? "positive" : undefined}
                              data-id="offer-detail-price"
                            >
                              ${formatWholeOrDecimalCurrency(price)}
                            </Heading>
                          </Hidden>
                          {hasSavings && (
                            <Text tone="neutralLight">
                              <s data-id="offer-detail-list-price">
                                ${formatWholeOrDecimalCurrency(listPrice)}
                              </s>
                            </Text>
                          )}
                        </Inline>
                        {hasSavings && (
                          <Status
                            backgroundWeight="strong"
                            tone="promoteD"
                            weight="medium"
                            data-id="offer-detail-savings"
                          >
                            {`Save $${formatWholeOrDecimalCurrency(discount)}`}
                          </Status>
                        )}
                      </Stack>
                    </Stack>
                  </Column>

                  {/* Divider — only when equipmentFee is present */}
                  {Number(equipmentFee) > 0 ? (
                    <Column width="content" alignSelf="stretch">
                      <Divider variant="vertical" />
                      <Hidden below="lg">
                        <Box paddingY={{ xs: "none", lg: "large" }}>
                          <Divider />
                        </Box>
                      </Hidden>
                    </Column>
                  ) : null}

                  {/* Annual fee — only when equipmentFee is present */}
                  {Number(equipmentFee) > 0 ? (
                    <Column>
                      <Stack
                        alignX={{ xs: "left", lg: "center" }}
                        space="small"
                        data-id="offer-detail-fee-label-row"
                      >
                        <Inline alignY="center" space="xsmall">
                          <Text
                            variant="subtitle-small"
                            tone="neutralDark"
                            data-id="offer-detail-fee-label"
                          >
                            {isRefill
                              ? content.annualFeeLabel.refill
                              : content.annualFeeLabel.exchange}
                          </Text>
                          <Tooltip
                            id="offer-detail-annual-fee-tooltip"
                            aria-label={
                              isRefill
                                ? content.annualFeeLabel.refill
                                : content.annualFeeLabel.exchange
                            }
                            color="primary"
                            noIconPadding
                          >
                            {isRefill
                              ? content.annualFeeTooltip.refill
                              : content.annualFeeTooltip.exchange}
                          </Tooltip>
                        </Inline>
                        <Stack
                          space="xxsmall"
                          alignX={{ xs: "left", lg: "center" }}
                        >
                          <Hidden above="lg">
                            <Box paddingTop="small">
                              <Heading
                                component="p"
                                variant="h2"
                                data-id="offer-detail-fee"
                              >
                                ${formatWholeOrDecimalCurrency(equipmentFee)}
                              </Heading>
                            </Box>
                          </Hidden>
                          <Hidden below="lg">
                            <Heading
                              component="p"
                              variant="h3"
                              weight="regular"
                              data-id="offer-detail-fee"
                            >
                              ${formatWholeOrDecimalCurrency(equipmentFee)}
                            </Heading>
                          </Hidden>
                          <Text
                            tone="neutralLight"
                            data-id="offer-detail-fee-per-bottle"
                          >
                            {content.perOnsiteBottle}
                          </Text>
                        </Stack>
                      </Stack>
                    </Column>
                  ) : null}
                </Columns>
              ) : null}
            </Card>

            {/* Buttons — desktop only */}
            <Hidden below="lg">
              <OfferButtonSection
                id={salesforceId}
                setAddressSelectorError={setAddressSelectorError}
              />
            </Hidden>
          </Stack>
        </Column>
      </Columns>
    </Card>
  );
};
